import React, { Component, useCallback, useEffect, useMemo, useState } from 'react';
import { classifyDashboardForbidden } from './dashboardForbidden';
import type { PortalBootstrapData } from './types/window';
import { __ } from '@/hub-shared/utils/i18n';
import CompaniesApp from '../../companies/src/CompaniesApp';
import UsersApp from '../../users/src/UsersApp';
import { AdminQueryClientProvider } from '@tpct/admin-ui';
import ProfilePage from './ProfilePage';
import NotificationsPage from './NotificationsPage';
import PortalSidebar from './PortalSidebar';
import VsmePortalPage from './VsmePortalPage';
import ConnectionsPage from './ConnectionsPage';
import CompanyPageApp from '../../company-page/src/CompanyPageApp';
import CompanyImportFromProvidersPage from './CompanyImportFromProvidersPage';
import CabinetPage from './CabinetPage';
import TrialBanner from '@/hub-shared/components/TrialBanner';
import { fetchWithNonce } from './utils/fetchWithNonce';
import MissionPortalPage from './MissionPortalPage';
import LegacyVsmeReportDossierRedirect from './components/LegacyVsmeReportDossierRedirect';
import { parseApiError } from './utils/parseApiError';
import {
  buildPathWithUpdatedSearch,
  type PortalSearchSyncFn,
} from './utils/portalUrlState';
import {
  parseLegacyVsmeReportDossierId,
  parseVsmeMissionIdFromPath,
  resolveLegacyVsmeRedirect,
} from '@tpct/mission-core/utils/vsmePortalRoutes';

type RouteId =
  | 'dashboard'
  | 'companies'
  | 'companies-import-from-provider'
  | 'company-detail'
  | 'users'
  | 'connections'
  | 'profile'
  | 'notifications'
  | 'cabinet'
  | 'vsme-mission-detail'
  | 'vsme-mission-setup'
  | 'vsme-missions'
  | 'vsme-dossier'
  | 'vsme-dossier-legacy-report'
  | 'vsme-dashboard'
  | 'vsme-reports'
  | 'vsme-diligence'
  | 'vsme-settings'
  | 'logout'
  | 'not-found';

type SessionState = 'checking' | 'active' | 'expired';

type PortalAppProps = {
  bootstrap: PortalBootstrapData;
};

type ErrorBoundaryProps = {
  children: React.ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

type DashboardTool = {
  id: string;
  title: string;
  description: string;
  url: string;
};

type DashboardWidget = {
  id: string;
  title: string;
  content: string;
};

type DashboardPayload = {
  subscription: {
    planId: string | null;
    reasonCode: string;
    has_active_subscription: boolean;
    message: string;
  };
  tools: DashboardTool[];
  widgets: DashboardWidget[];
};

type DashboardState = 'idle' | 'loading' | 'success' | 'error' | 'pending-review';
type VerificationNoticeState = 'idle' | 'loading' | 'success' | 'error';

type EmailVerificationState = {
  required: boolean;
  verified: boolean;
  canResend: boolean;
  cooldown: number;
  resendUrl: string;
  userEmail: string;
};

const normalizePath = (value: string) => {
  if (value.length > 1 && value.endsWith('/')) {
    return value.slice(0, -1);
  }
  return value;
};

const escapePathForRegex = (value: string) =>
  normalizePath(value).replace(/[/\\^$*+?.()|[\]{}]/g, '\\$&');

const resolveRouteId = (pathname: string, portalPath: string): RouteId => {
  const current = normalizePath(pathname);
  const base = normalizePath(portalPath);
  const escapedBase = escapePathForRegex(portalPath);

  if (current === base) {
    return 'dashboard';
  }
  if (current === `${base}/companies/import-from-provider`) {
    return 'companies-import-from-provider';
  }
  if (current === `${base}/companies`) {
    return 'companies';
  }
  const companyDetailCanonicalRe = new RegExp(`^${escapedBase}/companies/company/(\\d+)$`);
  if (companyDetailCanonicalRe.test(current)) {
    return 'company-detail';
  }
  const companyDetailLegacyRe = new RegExp(`^${escapedBase}/company/(\\d+)$`);
  if (companyDetailLegacyRe.test(current)) {
    return 'company-detail';
  }
  if (current === `${base}/users`) {
    return 'users';
  }
  if (current === `${base}/profile`) {
    return 'profile';
  }
  if (current === `${base}/notifications`) {
    return 'notifications';
  }
  if (current === `${base}/cabinet`) {
    return 'cabinet';
  }
  if (current === `${base}/connections`) {
    return 'connections';
  }
  if (current === `${base}/vsme`) {
    return 'vsme-dashboard';
  }
  if (current === `${base}/vsme/missions`) {
    return 'vsme-missions';
  }
  if (current === `${base}/vsme/missions/new`) {
    return 'vsme-mission-setup';
  }
  if (new RegExp(`^${escapedBase}/vsme/missions/(\\d+)/setup$`).test(current)) {
    return 'vsme-mission-setup';
  }
  if (new RegExp(`^${escapedBase}/vsme/missions/(\\d+)/dossier-de-travail$`).test(current)) {
    return 'vsme-dossier';
  }
  if (new RegExp(`^${escapedBase}/vsme/missions/(\\d+)$`).test(current)) {
    return 'vsme-mission-detail';
  }
  if (current === `${base}/vsme/reports`) {
    return 'vsme-reports';
  }
  const vsmeReportsRe = new RegExp(`^${base.replace(/[/\\^$*+?.()|[\]{}]/g, '\\$&')}/vsme/reports/(\\d+)$`);
  if (vsmeReportsRe.test(current)) {
    return 'vsme-dossier-legacy-report';
  }
  if (current === `${base}/vsme/settings`) {
    return 'vsme-settings';
  }
  if (current === `${base}/logout`) {
    return 'logout';
  }
  if (current.startsWith(`${base}/`)) {
    return 'not-found';
  }

  return 'not-found';
};

const getLegacyVsmeReportId = (pathname: string, portalPath: string): number | null =>
  parseLegacyVsmeReportDossierId(pathname, portalPath);

const getCompanyIdFromPath = (pathname: string, portalPath: string): number | null => {
  const current = normalizePath(pathname);
  const escapedBase = escapePathForRegex(portalPath);
  const canonicalRe = new RegExp(`^${escapedBase}/companies/company/(\\d+)$`);
  let m = current.match(canonicalRe);
  if (!m) {
    const legacyRe = new RegExp(`^${escapedBase}/company/(\\d+)$`);
    m = current.match(legacyRe);
  }
  if (!m) return null;
  const id = parseInt(m[1], 10);
  return Number.isInteger(id) && id > 0 ? id : null;
};

const getVsmeMissionIdFromPath = (pathname: string, portalPath: string): number | null =>
  parseVsmeMissionIdFromPath(pathname, portalPath);

const getLegacyCompanyDetailId = (pathname: string, portalPath: string): number | null => {
  const current = normalizePath(pathname);
  const escapedBase = escapePathForRegex(portalPath);
  const legacyRe = new RegExp(`^${escapedBase}/company/(\\d+)$`);
  const m = current.match(legacyRe);
  if (!m) return null;
  const id = parseInt(m[1], 10);
  return Number.isInteger(id) && id > 0 ? id : null;
};

const parseCompanyIdFromSearch = (search: string): number | null => {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const raw = params.get('companyId');
  if (raw === null || raw === '') {
    return null;
  }
  const id = parseInt(raw, 10);
  return Number.isInteger(id) && id > 0 ? id : null;
};

class PortalErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  public static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  public render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="accounting-hub-notice" role="alert">
          <h2>{__('Une erreur est survenue.')}</h2>
          <p>{__('Rechargez la page pour continuer.')}</p>
        </div>
      );
    }

    return this.props.children;
  }
}

const PortalApp = ({ bootstrap }: PortalAppProps) => {
  const [pathname, setPathname] = useState<string>(window.location.pathname);
  const [search, setSearch] = useState<string>(window.location.search);
  const [sessionState, setSessionState] = useState<SessionState>('checking');
  const [dashboardState, setDashboardState] = useState<DashboardState>('idle');
  const [dashboardData, setDashboardData] = useState<DashboardPayload | null>(null);
  const [dashboardError, setDashboardError] = useState<string>('');
  const [verification, setVerification] = useState<EmailVerificationState>(() => ({
    required: bootstrap.emailVerification?.required ?? false,
    verified: bootstrap.emailVerification?.verified ?? true,
    canResend: bootstrap.emailVerification?.canResend ?? false,
    cooldown: bootstrap.emailVerification?.cooldown ?? 0,
    resendUrl: bootstrap.emailVerification?.resendUrl ?? '',
    userEmail: bootstrap.emailVerification?.userEmail ?? bootstrap.user?.email ?? '',
  }));
  const [verificationNoticeState, setVerificationNoticeState] = useState<VerificationNoticeState>('idle');
  const [verificationNoticeMessage, setVerificationNoticeMessage] = useState<string>('');
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  const portalPath = useMemo(() => normalizePath(bootstrap.portalPath), [bootstrap.portalPath]);
  const routeId = useMemo(() => resolveRouteId(pathname, portalPath), [pathname, portalPath]);
  const legacyCompanyDetailId = useMemo(
    () => getLegacyCompanyDetailId(pathname, portalPath),
    [pathname, portalPath],
  );
  const importCompanyId = useMemo(
    () => (routeId === 'companies-import-from-provider' ? parseCompanyIdFromSearch(search) : null),
    [routeId, search]
  );
  const vsmeMissionId = useMemo(
    () => getVsmeMissionIdFromPath(pathname, portalPath),
    [pathname, portalPath],
  );
  const legacyVsmeRedirect = useMemo(
    () => resolveLegacyVsmeRedirect(pathname, search, portalPath),
    [pathname, portalPath, search],
  );
  const legacyVsmeReportId = useMemo(
    () => getLegacyVsmeReportId(pathname, portalPath),
    [pathname, portalPath],
  );

  useEffect(() => {
    if (legacyVsmeRedirect === null) {
      return;
    }
    const nextUrl = `${legacyVsmeRedirect.pathname}${legacyVsmeRedirect.search}`;
    if (`${window.location.pathname}${window.location.search}` === nextUrl) {
      return;
    }
    window.history.replaceState({}, '', nextUrl);
    setPathname(window.location.pathname);
    setSearch(window.location.search);
  }, [legacyVsmeRedirect]);

  useEffect(() => {
    const onPopState = () => {
      setPathname(window.location.pathname);
      setSearch(window.location.search);
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    if (legacyCompanyDetailId === null) {
      return;
    }
    const nextPath = `${portalPath}/companies/company/${legacyCompanyDetailId}`;
    const searchSuffix = window.location.search;
    window.history.replaceState({}, '', `${nextPath}${searchSuffix}`);
    setPathname(window.location.pathname);
    setSearch(window.location.search);
  }, [legacyCompanyDetailId, portalPath]);

  useEffect(() => {
    const root = document.getElementById('accounting-hub-portal-root');
    if (!root) return;
    if (bootstrap.isTrial === true) {
      root.classList.add('has-trial-banner');
    } else {
      root.classList.remove('has-trial-banner');
    }
    return () => root.classList.remove('has-trial-banner');
  }, [bootstrap.isTrial]);

  useEffect(() => {
    const root = document.getElementById('accounting-hub-portal-root');
    if (!root) return;
    const className = 'acchub-portal-root--dossier-full';
    if (routeId === 'vsme-dossier') {
      root.classList.add(className);
    } else {
      root.classList.remove(className);
    }
    return () => root.classList.remove(className);
  }, [routeId]);

  const isVerificationCooldownActive = !verification.canResend && verification.cooldown > 0;

  useEffect(() => {
    if (!isVerificationCooldownActive) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setVerification((prev) => {
        if (prev.canResend || prev.cooldown <= 0) {
          return prev;
        }
        if (prev.cooldown <= 1) {
          return { ...prev, cooldown: 0, canResend: true };
        }
        return { ...prev, cooldown: prev.cooldown - 1 };
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isVerificationCooldownActive]);

  useEffect(() => {
    const controller = new AbortController();

    const runSessionProbe = async () => {
      try {
        const response = await fetchWithNonce(`${bootstrap.restUrls.wp}wp/v2/users/me`, {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            'X-WP-Nonce': bootstrap.nonce,
          },
          signal: controller.signal,
        });

        if (response.status === 401 || response.status === 403) {
          setSessionState('expired');
          return;
        }

        setSessionState('active');
      } catch {
        if (!controller.signal.aborted) {
          setSessionState('active');
        }
      }
    };

    runSessionProbe();

    return () => controller.abort();
  }, [bootstrap.nonce, bootstrap.restUrls.wp]);

  useEffect(() => {
    if (routeId === 'logout') {
      window.location.href = bootstrap.logoutUrl;
    }
  }, [bootstrap.logoutUrl, routeId]);

  useEffect(() => {
    if (sessionState !== 'active' || routeId !== 'dashboard') {
      return;
    }

    const controller = new AbortController();
    const run = async () => {
      setDashboardState('loading');
      setDashboardError('');

      try {
        const response = await fetchWithNonce(`${bootstrap.restUrls.accountingHub}dashboard`, {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            'X-WP-Nonce': bootstrap.nonce,
          },
          signal: controller.signal,
        });

        if (response.status === 401) {
          setSessionState('expired');
          return;
        }

        if (response.status === 403) {
          let payload: unknown = null;
          try {
            payload = await response.json();
          } catch {
            /* body may be empty or not JSON */
          }

          const forbidden = classifyDashboardForbidden(payload, __('Accès refusé.'));
          setDashboardError(forbidden.message);
          setDashboardState(forbidden.kind);
          return;
        }

        if (!response.ok) {
          setDashboardState('error');
          setDashboardError(await parseApiError(response));
          return;
        }

        const payload = (await response.json()) as DashboardPayload;
        setDashboardData(payload);
        setDashboardState('success');
      } catch {
        if (!controller.signal.aborted) {
          setDashboardState('error');
          setDashboardError(__('Impossible de charger le tableau de bord.'));
        }
      }
    };

    run();

    return () => controller.abort();
  }, [bootstrap.nonce, bootstrap.restUrls.accountingHub, routeId, sessionState]);

  const navigateTo = (targetPath: string) => {
    const nextPath = `${portalPath}${targetPath}`;
    if (normalizePath(window.location.pathname) === normalizePath(nextPath.split('?')[0] ?? nextPath)) {
      if (window.location.search === (nextPath.includes('?') ? nextPath.slice(nextPath.indexOf('?')) : '')) {
        return;
      }
    }

    window.history.pushState({}, '', nextPath);
    setPathname(window.location.pathname);
    setSearch(window.location.search);
  };

  const patchPortalSearch = useCallback<PortalSearchSyncFn>((updates, mode = 'push') => {
    const nextPathWithQuery = buildPathWithUpdatedSearch(
      window.location.pathname,
      window.location.search,
      updates,
    );
    const nextUrl = new URL(nextPathWithQuery, window.location.origin);
    const curUrl = new URL(window.location.href);
    if (nextUrl.pathname === curUrl.pathname && nextUrl.search === curUrl.search) {
      return;
    }
    const display = `${nextUrl.pathname}${nextUrl.search}`;
    if (mode === 'push') {
      window.history.pushState({}, '', display);
    } else {
      window.history.replaceState({}, '', display);
    }
    setPathname(window.location.pathname);
    setSearch(window.location.search);
  }, []);

  const handleResendVerificationEmail = async () => {
    if (!verification.resendUrl || verificationNoticeState === 'loading') {
      return;
    }

    setVerificationNoticeState('loading');
    setVerificationNoticeMessage(__('Envoi en cours...'));

    try {
      const response = await fetchWithNonce(verification.resendUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': bootstrap.nonce,
        },
      });

      const payload = (await response.json()) as { success?: boolean; data?: { message?: string; cooldown?: number }; message?: string; code?: string };
      const message = payload?.data?.message ?? payload?.message ?? __("Erreur lors de l'envoi");

      if (!response.ok || payload.success !== true) {
        setVerificationNoticeState('error');
        setVerificationNoticeMessage(message);
        if (typeof payload?.data?.cooldown === 'number') {
          setVerification((prev) => ({ ...prev, canResend: false, cooldown: payload.data?.cooldown ?? prev.cooldown }));
        }
        return;
      }

      setVerificationNoticeState('success');
      setVerificationNoticeMessage(message);
      setVerification((prev) => ({
        ...prev,
        canResend: false,
        cooldown: typeof payload.data?.cooldown === 'number' ? payload.data.cooldown : 60,
      }));
    } catch {
      setVerificationNoticeState('error');
      setVerificationNoticeMessage(__('Erreur de connexion'));
    }
  };

  const renderMainContent = () => {
    if (sessionState === 'expired') {
      return (
        <section aria-live="polite">
          <h2>{__('Session expiree')}</h2>
          <p>{__('Votre session a expire. Reconnectez-vous pour continuer.')}</p>
          <div className="acchub-button-group">
            <a href={bootstrap.loginUrl} className="button button-primary">
              {__('Se reconnecter')}
            </a>
            <button
              type="button"
              className="button"
              onClick={() => window.location.reload()}
            >
              {__('Recharger')}
            </button>
          </div>
        </section>
      );
    }

    if (sessionState === 'checking') {
      return (
        <section aria-live="polite">
          <h2>{__('Verification de session...')}</h2>
        </section>
      );
    }

    if (routeId === 'dashboard') {
      if (dashboardState === 'loading' || dashboardState === 'idle') {
        return (
          <div aria-live="polite">
            <h1 className="wp-heading-inline">{__('Tableau de bord')}</h1>
            <p>{__('Chargement des informations...')}</p>
          </div>
        );
      }

      if (dashboardState === 'pending-review') {
        return (
          <div aria-live="polite">
            <h1 className="wp-heading-inline">{__('Tableau de bord')}</h1>
            <div className="accounting-hub-notice notice-info" role="status">
              <p>{dashboardError}</p>
            </div>
          </div>
        );
      }

      if (dashboardState === 'error') {
        return (
          <div aria-live="polite">
            <h1 className="wp-heading-inline">{__('Tableau de bord')}</h1>
            <div className="accounting-hub-notice" role="alert">
              <p>{__('Le chargement a échoué. ')}{dashboardError}</p>
            </div>
            <button type="button" className="button" onClick={() => window.location.reload()}>
              {__('Réessayer')}
            </button>
          </div>
        );
      }

      const tools = dashboardData?.tools ?? [];
      const widgets = dashboardData?.widgets ?? [];
      const hasActiveSubscription = dashboardData?.subscription?.has_active_subscription ?? false;
      const subscriptionMessage = dashboardData?.subscription?.message ?? '';
      const planId = dashboardData?.subscription?.planId ?? null;
      const subscriptionNoticeClass = (() => {
        if (hasActiveSubscription) {
          return 'notice notice-success';
        }
        if (planId === 'trial' || planId === 'beta' || planId === 'memorialist') {
          return 'notice notice-info';
        }
        return 'notice notice-warning';
      })();

      return (
        <div>
          <h1 className="wp-heading-inline">{__('Tableau de bord')}</h1>

          <div className={subscriptionNoticeClass}>
            <p>{subscriptionMessage}</p>
          </div>

          <div className="accounting-hub-dashboard">
            {tools.length > 0 ? (
              <div className="accounting-hub-tools">
                <h2>{__('Outils disponibles')}</h2>
                <div className="tools-grid">
                  {tools.map((tool) => (
                    <div key={tool.id} className="tool-card">
                      <h3>{tool.title}</h3>
                      {tool.description ? <p>{tool.description}</p> : null}
                      {tool.url ? (
                        <a href={tool.url} className="button">
                          {__("Accéder à l'outil")}
                        </a>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="accounting-hub-dashboard-content">
              {widgets.map((widget) => (
                <div key={widget.id}>
                  <strong>{widget.title}</strong>
                  <div dangerouslySetInnerHTML={{ __html: widget.content }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (routeId === 'companies') {
      return (
          <CompaniesApp
            bootstrapKey="AccountingHubPortal"
            navigateTo={navigateTo}
            search={search}
            patchPortalSearch={patchPortalSearch}
          />
      );
    }

    if (routeId === 'companies-import-from-provider') {
      return (
          <CompanyImportFromProvidersPage
            navigateTo={navigateTo}
            bootstrap={bootstrap}
            companyId={importCompanyId}
            onSessionExpired={() => setSessionState('expired')}
            search={search}
            patchPortalSearch={patchPortalSearch}
          />
      );
    }

    if (routeId === 'company-detail') {
      const companyId = getCompanyIdFromPath(pathname, portalPath);
      return (
          <CompanyPageApp
            companyId={companyId ?? 0}
            bootstrapKey="AccountingHubPortal"
            navigateTo={navigateTo}
            search={search}
            patchPortalSearch={patchPortalSearch}
          />
      );
    }

    if (routeId === 'users') {
      return (
          <UsersApp
            bootstrapKey="AccountingHubPortal"
            navigateTo={navigateTo}
            search={search}
            patchPortalSearch={patchPortalSearch}
          />
      );
    }

    if (routeId === 'connections') {
      return (
          <ConnectionsPage
            bootstrap={bootstrap}
            onSessionExpired={() => setSessionState('expired')}
            search={search}
            patchPortalSearch={patchPortalSearch}
          />
      );
    }

    if (routeId === 'profile') {
      return (
          <ProfilePage
            bootstrap={bootstrap}
            onSessionExpired={() => setSessionState('expired')}
            search={search}
            patchPortalSearch={patchPortalSearch}
          />
      );
    }

    if (routeId === 'notifications') {
      return (
          <NotificationsPage
            bootstrap={bootstrap}
            onSessionExpired={() => setSessionState('expired')}
            onUnreadCountChange={setUnreadNotificationCount}
          />
      );
    }

    if (routeId === 'cabinet') {
      return (
          <CabinetPage
            bootstrap={bootstrap}
            onSessionExpired={() => setSessionState('expired')}
            search={search}
            patchPortalSearch={patchPortalSearch}
          />
      );
    }

    if (routeId === 'vsme-mission-detail' && vsmeMissionId !== null) {
      return (
        <MissionPortalPage
          bootstrap={bootstrap}
          missionId={vsmeMissionId}
          onNavigate={navigateTo}
        />
      );
    }

    if (routeId === 'vsme-dashboard') {
      return (
          <VsmePortalPage bootstrap={bootstrap} mode="dashboard" />
      );
    }

    if (routeId === 'vsme-missions' || routeId === 'vsme-mission-setup' || routeId === 'vsme-reports') {
      return (
          <VsmePortalPage bootstrap={bootstrap} mode="reports" />
      );
    }

    if (routeId === 'vsme-dossier' && vsmeMissionId !== null) {
      return (
        <section>
          <VsmePortalPage
            key={`dossier-${vsmeMissionId}-${search}`}
            bootstrap={bootstrap}
            mode="dossier"
            missionId={vsmeMissionId}
            locationSearch={search}
          />
        </section>
      );
    }

    if (routeId === 'vsme-dossier-legacy-report' && legacyVsmeReportId !== null) {
      return (
        <LegacyVsmeReportDossierRedirect
          bootstrap={bootstrap}
          reportId={legacyVsmeReportId}
        />
      );
    }

    if (routeId === 'vsme-settings') {
      return (
        <section>
          <VsmePortalPage bootstrap={bootstrap} mode="settings" />
        </section>
      );
    }

    if (routeId === 'logout') {
      return (
        <section>
          <h2>{__('Deconnexion')}</h2>
          <p>{__('Redirection en cours...')}</p>
        </section>
      );
    }

    return (
      <section>
        <h2>{__('Page introuvable')}</h2>
        <p>{__("Cette route n'existe pas dans le portail.")}</p>
        <button type="button" className="button button-primary" onClick={() => navigateTo('')}>
          {__('Retour au tableau de bord')}
        </button>
      </section>
    );
  };

  return (
    <PortalErrorBoundary>
      {bootstrap.isTrial === true && (
        <TrialBanner subscriptionTierUrl={bootstrap.subscriptionTierUrl} />
      )}

          <aside className="acchub-sidebar-wrapper">
            <PortalSidebar
              bootstrap={bootstrap}
              pathname={pathname}
              onNavigate={navigateTo}
              unreadNotificationCount={unreadNotificationCount}
              onUnreadNotificationCountChange={setUnreadNotificationCount}
            />
          </aside>

          <main className="acchub-portal-main">
            {sessionState === 'active' && verification.required && !verification.verified ? (
              <section className="acchub-portal-verification-banner" role="alert" aria-live="polite">

                <div className="acchub-portal-verification-banner__content">
                  <p className="acchub-portal-verification-banner__title">{__("Verification de l'email requise")}</p>
                  <div className="acchub-portal-verification-banner__message-container">
                    <p className="acchub-portal-verification-banner__message">
                      {__('Un email a été envoyé à')} <strong>{verification.userEmail}</strong>. {__('Cliquez sur le lien de verification pour activer votre accès aux outils.')}
                    </p>
                    <div className="acchub-portal-verification-banner__actions">
                    {verification.canResend ? (
                      <button
                        type="button"
                        className="btn-primary"
                        onClick={handleResendVerificationEmail}
                        disabled={verificationNoticeState === 'loading'}
                      >
                        {verificationNoticeState === 'loading' ? __('Envoi en cours...') : __("Renvoyer l'email")}
                      </button>
                    ) : (
                      <button type="button" className="btn-secondary" disabled>
                        {verification.cooldown > 0 ? `${__('Réessayer dans')} ${verification.cooldown}s` : __('Email envoyé')}
                      </button>
                    )}
                    {verificationNoticeState === 'success' || verificationNoticeState === 'error' ? (
                      <span
                        className={`acchub-portal-verification-banner__feedback ${
                          verificationNoticeState === 'error'
                            ? 'acchub-portal-verification-banner__feedback--error'
                            : 'acchub-portal-verification-banner__feedback--success'
                        }`}
                      >
                        {verificationNoticeMessage}
                      </span>
                    ) : null}
                  </div>
                  </div>

                </div>
              </section>
            ) : null}
            <AdminQueryClientProvider>{renderMainContent()}</AdminQueryClientProvider>
          </main>
    </PortalErrorBoundary>
  );
};

export default PortalApp;
